import * as React from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import TagPlate from '../TagPlate/TagPlate';
import './Details.scss';

import { ReactComponent as PlateIcon } from '@assets/images/plate-icon.svg';
import { FlowcellControl, IExperimentResult } from '@models/upload';

import '@styles/components/table.scss';

export interface IDetailsStateProps {
  experiment: IExperimentResult;
  isCollapsible?: boolean;
  newExperiment?: boolean;
}

const Details: React.FC<IDetailsStateProps> = ({
  experiment,
  isCollapsible,
  newExperiment = false,
}) => {
  const elementRef = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const [isCollapsed, setIsCollapsed] = React.useState(isCollapsible);

  const tagPlates = experiment.tagPlates;

  const samplesCount = _.sum(tagPlates.map((plate) => plate.samples.length));

  const controlsCount = _.sum(
    tagPlates.map(
      (plate) =>
        plate.samples.filter(
          (sample) =>
            sample.controlType === FlowcellControl.positive ||
            sample.controlType === FlowcellControl.negative
        ).length
    )
  );

  const totalWellCount = _.sum(
    tagPlates.map((t) => _.sum(t.samples.map((s) => s.wells.length)))
  );

  const selectTagPlate = (index: number): void => {
    if (tagPlates.length === 1) {
      return;
    }

    const current = elementRef?.current;
    if (!current) {
      return;
    }

    const component: HTMLElement | null | undefined =
      current.querySelector<HTMLElement>(
        `.tag-plates > div:nth-child(${index})`
      );

    if (!component) {
      return;
    }

    component.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  };

  return (
    <div ref={elementRef}>
      <hr className='m-0' />
      <div className='font-weight-bold fcolor--text-label py-2'>
        {isCollapsible ? (
          <span
            onClick={() => setIsCollapsed(!isCollapsed)}
            className='cursor-pointer hover-underline'
          >
            {isCollapsed && (
              <span>
                <i className={'fas fa-caret-right mr-1'}></i>
              </span>
            )}
            {!isCollapsed && (
              <span>
                <i className={'fas fa-caret-down mr-1'}></i>
              </span>
            )}
            {t('common_details')}
          </span>
        ) : (
          <span>{t('common_details')}</span>
        )}
      </div>
      {!isCollapsed && (
        <div className='details'>
          <table className='table-base table-denser-data'>
            <thead>
              <tr>
                <th>{t('upload_totalSamples')}</th>
                <th>{t('upload_totalWells')}</th>
                <th>
                  {experiment.kind === 'mrd'
                    ? t('upload_tagPlates')
                    : t('upload_kitSN')}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{`${t('upload_samples', { count: samplesCount })} ${t(
                  'upload_controls',
                  { count: controlsCount }
                )}`}</td>
                <td>{t('upload_wells', { count: totalWellCount })}</td>
                <td>
                  {tagPlates.map((t, i) => (
                    <React.Fragment key={`${t}${i}link`}>
                      <span
                        onClick={() => selectTagPlate(i + 1)}
                        className={`pr-1 ${
                          tagPlates.length > 1
                            ? 'link'
                            : 'font-weight-semi-bold'
                        }`}
                      >
                        <PlateIcon className='plate-icon' />
                        <span className='align-middle'>
                          {'kitSN' in t ? t.kitSN : t.plateLayout}
                        </span>
                      </span>
                    </React.Fragment>
                  ))}
                </td>
              </tr>
            </tbody>
          </table>

          <div className='tag-plates'>
            {tagPlates.map((tagPlate, i) => {
              return (
                <TagPlate
                  newExperiment={newExperiment}
                  samples={
                    // This is to display the tag plate for non mrd
                    // experiments until we can render non mdr
                    // experiments correctly. We can either
                    // parameterize the TagPlate component similar to
                    // react-table library or use a new component.
                    'kitSN' in tagPlate
                      ? tagPlate.samples.map((sample) => {
                          return {
                            ...sample,
                            externalSubjectId: sample.name,
                            properties: undefined,
                            testType: null,
                          };
                        })
                      : tagPlate.samples
                  }
                  plateLayout={tagPlate.plateLayout}
                  key={`${tagPlate.plateLayout}${i}`}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Details;
